import styles from '@components/Card2/Card/Card.module.scss';
import { CardItemEditor } from '@components/Card2/CardItemEditor';
import { canDeleteCardItem } from '@components/Card2/utils';
import { cardEditContext } from '@context/card-edit-context';
import { cardModeContext } from '@context/card-mode-context';
import { useCardPhoneFieldState } from '@hooks/use-card-field-state';
import useNode from '@hooks/use-node';
import { logger } from '@modules/logger';
import { useContext, useState } from 'react';

import PhoneNumberFields from '../../../CardFormFields/PhoneNumberFields/PhoneNumberFields';
import type { RowProps } from '../../types';
import { getHref, getNewCardFieldLexorank, useCardFieldMutations } from '../../utils';
import { FieldEditor } from '../FieldEditor';
import { FieldRow } from '../FieldRow';
import PhoneNumberContent from './Content';
import type { CardField, ValueProvider } from '.cache/__types__';

export function PhoneNumber({ cardId, field, index, size, ...restProps }: RowProps): JSX.Element {
  const [fieldNode, fieldRef] = useNode<HTMLDivElement | HTMLButtonElement>();
  const [fieldBtnNode, fieldBtnRef] = useNode<HTMLDivElement | HTMLButtonElement>();
  const [tempValueProviderForTour, setTempValueProviderForTour] = useState<ValueProvider | null>(null);

  const cardFieldMutations = useCardFieldMutations(cardId, field);
  const { editPageMode, setPendingCardField, activeAttribute, flavour } = useContext(cardEditContext);
  const { mode } = useContext(cardModeContext);

  const fieldState = useCardPhoneFieldState(field, 'member');

  const onSave = (newValueProvider?: ValueProvider) => {
    const stage = fieldState.value ? 'done' : 'todo';

    const sharedMutationValues: Omit<CardField, 'id' | 'attribute_type_id'> & { card_id: string } = {
      card_id: cardId,
      label: fieldState.label,
      label_locked: fieldState.labelLocked,
      is_optional: fieldState.isOptional,
      value: fieldState.value,
      phone_country_code: fieldState.phoneCountryCode,
      phone_extension: fieldState.phoneExtension,
      value_provider: fieldState.isModified // Prevent updating the value provider if the user hasn't made any changes
        ? (newValueProvider ?? fieldState.valueProvider)
        : fieldState.valueProvider,
      value_locked: fieldState.valueLocked,
      instruction_text: fieldState.instruction ?? null,
      stage,
    };

    if (editPageMode === 'create') {
      if (!field.attribute_type?.id) {
        logger.error('No attribute type id found for address field');
        return;
      }

      cardFieldMutations.create(
        {
          ...sharedMutationValues,
          lexorank: getNewCardFieldLexorank(cardId),
          attribute_type_id: field.attribute_type?.id,
        },
        activeAttribute,
      );
      return;
    }

    if (!field.lexorank) {
      logger.error('No lexorank found for phone number field');
      return;
    }

    cardFieldMutations.update({
      ...sharedMutationValues,
      lexorank: field.lexorank,
      id: field.id,
    });
  };

  const canDelete = canDeleteCardItem({
    flavour,
    isOptional: Boolean(field.is_optional),
    isTemplated: Boolean(field.template_id),
    valueProvider: field.value_provider,
  });

  const handleDelete = () => {
    if (!canDelete) return;
    cardFieldMutations.remove({
      card_id: cardId,
      id: field.id,
    });
  };

  const handleCancel = () => {
    fieldState.onCancel();
    setPendingCardField(undefined);
  };

  if (mode === 'clickable') {
    return (
      <a href={getHref(field)} className={styles['card-item']} rel="noreferrer">
        <PhoneNumberContent
          number={field.value ?? ''}
          extension={field.phone_extension ?? ''}
          label={field.label ?? ''}
          size={size}
        />
      </a>
    );
  }

  if (mode === 'read-only') {
    return (
      <div className={styles['card-item']} rel="noreferrer" style={{ cursor: 'default' }}>
        <PhoneNumberContent
          number={field.value ?? ''}
          extension={field.phone_extension ?? ''}
          label={field.label ?? ''}
          size={size}
        />
      </div>
    );
  }

  return (
    <>
      <FieldRow
        id={field.id}
        fieldValue={field.value ?? ''}
        index={index}
        isLocked={fieldState.fullyLocked}
        isLockedTooltipMessage={fieldState.lockedTooltipMessage}
        isOptional={field.is_optional}
        fieldBtnRef={fieldBtnRef}
        fieldRef={fieldRef}
        {...restProps}
      >
        <div className={styles['card-item']} data-edit-mode>
          <PhoneNumberContent
            number={fieldState.value}
            extension={fieldState.phoneExtension}
            label={fieldState.label}
            size={size}
          />
        </div>
      </FieldRow>

      <FieldEditor
        title={field.attribute_type?.display_name || ''}
        id={field.id}
        updateTempValueProviderForTour={setTempValueProviderForTour}
        fieldBtnNode={fieldBtnNode}
        fieldNode={fieldNode}
        hasUnsavedChanges={field.value !== fieldState.value}
        positionDeps={[]}
        onCancel={handleCancel}
        onDelete={handleDelete}
        onSave={onSave}
        validator={() => true}
        isFieldLinkedToProfile={fieldState.valueProvider === 'profile'}
        unlinkCardField={onSave}
        canDelete={canDelete}
      >
        <CardItemEditor
          {...fieldState}
          tempValueProviderForTour={tempValueProviderForTour}
          showLabelWhenIndividual
          fieldType="phone"
        >
          <PhoneNumberFields {...fieldState} />
        </CardItemEditor>
      </FieldEditor>
    </>
  );
}
